import React, { useEffect, useRef, useState } from "react";
import DynamicForm from "./DynamicForm";
import { buyerAgent, buyerWithAgentRole } from "./formFields";
import { BsCheckCircleFill } from "react-icons/bs";
import { verifyLicense } from "@/api/placeOffer";
import { formatDisplayDate } from "@/utils/dateFormatter";
import { validateAddress } from "@/api/bid";

const BuyersAgent = ({
  cifFormetails,
  setCifFormDetails,
  setAccordionState,
  accordionState,
  profileDetails,
  state,
  pof,
  validateEmail,
  propertyId,
  buyerAgentState,
  buyerAgentDetails,
  setBuyerAgentDetails,
}) => {
  // const [buyerAgentDetails, setBuyerAgentDetails] = useState({});
  const [haveErrors, setHaveErrors] = useState(false);
  const validationFnRef = useRef(null);
  const [error, setError] = useState({});
  const [externalFormErrors, setExternalFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const convertToUIDate = (dateString) => {
    try {
      if (typeof dateString !== "string") return "";
      const parts = dateString.split("/");
      if (parts.length === 3) {
        const [month, day, year] = parts;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
      return "";
    } catch (error) {
      console.error("Invalid date format:", error);
      return "";
    }
  };

  useEffect(() => {
    if (buyerAgent) {
      const matchedLicense = profileDetails?.agentLicenseInfo?.find(
        (item) => item?.stateDto?.stateId === state
      );
      
      setBuyerAgentDetails((prev) => {
        return {
          ...prev,
          individualLicenseState: state || "",
          firstName:
            prev.firstName || profileDetails?.userDetails?.firstName || "",
          lastName:
            prev.lastName || profileDetails?.userDetails?.lastName || "",
          middleInitials:
            prev.middleInitials ||
            profileDetails?.userDetails?.middleInitials ||
            "",
          email: prev.email || profileDetails?.userDetails?.emailId || "",
          phone: prev.phone || "",
          address1:
            prev.address1 ||
            profileDetails?.userDetails?.address?.address1 ||
            "",
          address2:
            prev.address2 ||
            profileDetails?.userDetails?.address?.address2 ||
            "",
          stateId:
            prev.stateId || profileDetails?.userDetails?.address?.state || "",
          city: prev.city || profileDetails?.userDetails?.address?.city || "",
          zip: prev.zip || profileDetails?.userDetails?.address?.zip || "",
          corpLicenseState: prev.corpLicenseState || "",
          corpLicenseNumber: prev.corpLicenseNumber || "",
          individualLicenseNumber:
            prev.individualLicenseNumber || matchedLicense?.licenseNumber || "",
          licenseExpiryDate:
            prev.licenseExpiryDate ||
            convertToUIDate(matchedLicense?.licenseExpiryDate) ||
            "",
          company: prev.company || profileDetails?.companyProfileDto?.brokerageName || matchedLicense?.afftCompanyName || "",
        };
      });
    }
  }, [buyerAgent, profileDetails, state]);

  const checkStateCityZip = async () => {
    try {
      setLoading(true);
      const payload = {
        address1: buyerAgentDetails?.address1,
        city: buyerAgentDetails?.city,
        state: buyerAgentDetails?.stateId,
        zip: buyerAgentDetails?.zip,
      };
      const data = await validateAddress(payload);
      if (data?.data?.errorMessage === "VALIDATION_STATE_ZIP_FAILURE") {
        setLoading(false);
        // setInvalidCombination(true);
        setExternalFormErrors((prev) => ({
          ...prev,
          zip: "Provided zip code is not valid for selected state",
        }));
        setHaveErrors(true);
        return { hasError: true };
      } else {
        setLoading(false);
        setExternalFormErrors((prev) => {
          const { zip, ...rest } = prev;
          return rest;
        });
        return { hasError: false };
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };
  const licenseVerification = async () => {
    try {
      setLoading(true);
      const payload = {
        licenceNumber: buyerAgentDetails?.individualLicenseNumber,
        stateId: buyerAgentDetails?.stateId,
        affiliatedCompany: buyerAgentDetails?.company,
        agentEmailId: buyerAgentDetails?.email,
        agentFirstName: buyerAgentDetails?.firstName,
        agentLastName: buyerAgentDetails?.lastName,
        licenceExpiryDate: formatDisplayDate(
          buyerAgentDetails?.licenseExpiryDate
        ),
        agentLicenseState: buyerAgentDetails?.individualLicenseState,
        tempParam: propertyId,
      };
      const data = await verifyLicense(payload);
      if (data?.verificationStatus === "LICENSE_FORMAT_NOT_OK") {
        setLoading(false);
        setExternalFormErrors((prev) => ({
          ...prev,
          individualLicenseNumber: "Invalid license format",
        }));
        return { hasError: true };
      } else {
        setLoading(false);
        setExternalFormErrors((prev) => {
          const { individualLicenseNumber, ...rest } = prev;
          return rest;
        });
        return { hasError: false };
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };
  const submitData = async () => {
    setLoading(true);
    if (validationFnRef.current) {
      const { isValid, data, errors } = validationFnRef.current();

      const corpState = data?.corpLicenseState?.trim();
      const corpNumber = data?.corpLicenseNumber?.trim();

      const isCorpStateFilled = !!corpState;
      const isCorpNumberFilled = !!corpNumber;

      if (
        (isCorpStateFilled && !isCorpNumberFilled) ||
        (!isCorpStateFilled && isCorpNumberFilled)
      ) {
        setError({
          ...errors,
          corporateLicenseState:
            "You can skip entering the corp license state and number or provide both",
        });
        setLoading(false);
        return;
      }

      if (!isValid) {
        setLoading(false);
        return;
      }
      if (isValid) {
        const add = await checkStateCityZip();
        if (add?.hasError) {
          return;
        }
        const licenseVerificationResult = await licenseVerification();
        if (licenseVerificationResult?.hasError) {
          return;
        }
        setError({});
        // setCifFormDetails({
        //   ...cifFormetails,
        //   buyerAgent: buyerAgentDetails,
        // });
        setLoading(false);
        setCifFormDetails({
          ...cifFormetails,
          buyerAgent: {
            ...buyerAgentDetails,
            licenseExpiryDate: formatDisplayDate(
              buyerAgentDetails?.licenseExpiryDate
            ),
          },
        });

        const findNextAccordion = () => {
          if (pof === "Optional" || pof === "Required") {
            setAccordionState({
              ...accordionState,
              accordion3: {
                ...accordionState.accordion3,
                close: true,
                visited: true,
                edit: true,
              },
              accordion4: {
                ...accordionState.accordion4,
                close: false,
                edit: false,
              },
            });
          } else {
            setAccordionState({
              ...accordionState,
              accordion3: {
                ...accordionState.accordion3,
                close: true,
                visited: true,
                edit: true,
              },
              accordion5: {
                ...accordionState.accordion5,
                close: false,
              },
            });
          }
        };

        findNextAccordion();
      }
    }
  };

  return (
    <div className="accordion-item mb-2 border rounded">
      <h2 className="accordion-header p-1" id="flush-headingThree">
        <div
          className={`accordion-button ${
            accordionState?.accordion3?.close ? "collapsed" : ""
          }  ${
            accordionState?.accordion3?.close &&
            accordionState?.accordion3?.visited
              ? "no-caret"
              : ""
          }`}
          // type="button"
          // data-bs-toggle="collapse"
          // data-bs-target="#flush-collapseThree"
          // aria-expanded="false"
          id="acc2"
          // aria-controls="flush-collapseThree"
          disabled={accordionState?.accordion3?.close}
        >
          <div className="d-flex justify-content-between w-100">
            <p className="mb-0 responsive-heading">
              {accordionState?.accordion3?.visited ? (
                <BsCheckCircleFill className="icon_bid me-2" size={30} />
              ) : (
                <BsCheckCircleFill className="text-dark me-2" size={30} />
              )}
              Buyer's Agent
            </p>
            {accordionState?.accordion3?.edit && (
              <>
                <div>
                  <span
                    className="fa fa-pencil cursor-pointer"
                    onClick={() => {
                      if (pof === "Optional" || pof === "Required") {
                        setAccordionState({
                          ...accordionState,
                          accordion2: {
                            ...accordionState.accordion2,
                            close: true,
                            visited: true,
                            edit: true,
                          },
                          accordion3: {
                            ...accordionState.accordion3,
                            edit: false,
                            close: false,
                            visited: false,
                          },
                          accordion4: {
                            ...accordionState.accordion4,
                            close: true,
                            visited: false,
                            edit: true,
                          },
                        });
                      } else {
                        setAccordionState({
                          ...accordionState,
                          accordion2: {
                            ...accordionState.accordion2,
                            close: true,
                            visited: true,
                            edit: true,
                          },
                          accordion3: {
                            ...accordionState.accordion3,
                            edit: false,
                            close: false,
                            visited: false,
                          },
                          accordion5: {
                            ...accordionState.accordion5,
                            close: true,
                            visited: false,
                            edit: true,
                          },
                        });
                      }
                    }}
                  ></span>
                </div>
              </>
            )}
          </div>
        </div>
      </h2>
      {accordionState?.accordion3?.edit ? (
        <>
          <div className="container px-3 px-sm-2">
            <div className="row">
              <div className="col-6">
                <p className="edit-buyer-data">Buyer's Agent :-</p>
                <p className="edit-buyer-data mb-0">
                  {buyerAgentDetails?.firstName} {buyerAgentDetails?.lastName}
                </p>
                <p className="edit-buyer-data">{buyerAgentDetails?.email}</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div
          id="flush-collapseThree"
          className={`accordion-collapse collapse ${
            accordionState?.accordion3?.close ? "" : "show"
          }`}
          aria-labelledby="flush-headingThree"
          data-bs-parent="#accordionFlushExample"
        >
          <div className="accordion-body mobile-padding-0-offer">
            <div className="buyer-form">
              <DynamicForm
                fieldsConfig={
                  cifFormetails?.userRole === "Agent"
                    ? buyerWithAgentRole
                    : buyerAgent
                }
                setCifFormDetails={setBuyerAgentDetails}
                cifFormetails={buyerAgentDetails}
                preFilledEmail={
                  profileDetails?.userDetails?.emailId ? true : false
                }
                setHaveErrors={setHaveErrors}
                validateAgentEmail={validateEmail}
                statePrefilled={state}
                registerValidationTrigger={(fn) =>
                  (validationFnRef.current = fn)
                }
                externalErrors={externalFormErrors}
                setExternalFormErrors={setExternalFormErrors}
              />
            </div>
            {error?.corporateLicenseState?.length > 0 && (
              <p className={`error-message-offer `}>
                {error?.corporateLicenseState}
              </p>
            )}

            <button
              type="submit"
              className="continue-button ml10"
              onClick={submitData}
            >
              {loading ? "Continue..." : "Continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyersAgent;
